import React from "react"
import { useTranslation } from "react-i18next"
import { faBrain, faClose, faCloud, faKey, faLink, faRobot } from "@fortawesome/free-solid-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import ModalPortal from "../../../../components/Modal/ModalPortal"
import Animate, { AnimationTypes } from "../../../../components/Animate/Animate"
import defaultStyle from "../../../../styles/defaultPageStyle.module.css"
import { TitlePageResponsiveIconSmall } from "../../../../components/Titles/Titles"
import THEME from "../../../../configuration/Theme"
import { SIZE } from "../../../../configuration/Sizes"
import { ButtonIcon } from "../../../../components/Button/Button"
import InputField from "../../../../components/InputField/InputField"
import { SimpleToggle } from "../../../../components/SimpleToggle/SimpleToggle"
import { PopupHelpButton } from "../../../../components/Help/PopupHelpButton"
import { EAiIntegrationProvider } from "../../../../Objects/AiIntegration/EAiIntegrationProvider"
import { IAiIntegrationSettings } from "../../../../Objects/AiIntegration/IAiIntegrationSettings"

type Props = {
  isOpen: boolean
  settings: IAiIntegrationSettings
  onChange: (settings: IAiIntegrationSettings) => void
  onClose: () => void
  onSave: () => void
}

export const AiIntegrationSettingsPopup: React.FC<Props> = ({ isOpen, settings, onChange, onClose, onSave }) => {
  const { t } = useTranslation(["pages", "buttons", "helpPopup"])

  if (!isOpen) {
    return null
  }

  const patch = (next: Partial<IAiIntegrationSettings>) => onChange({ ...settings, ...next })

  const applyGptPreset = () =>
    patch({
      provider: EAiIntegrationProvider.Gpt,
      endpointUrl: "https://api.openai.com/v1/chat/completions",
      model: "gpt-4o-mini",
    })

  const applyCloudPreset = () =>
    patch({
      provider: EAiIntegrationProvider.Cloud,
      endpointUrl: "https://sua-cloud.com/api/ai/metrics",
      model: "cloud-metrics",
    })

  const patchMonths = (value: string) => {
    const parsed = Number(value)

    patch({ monthsToAnalyze: Number.isFinite(parsed) ? parsed : 24 })
  }

  return (
    <ModalPortal>
      <div className={defaultStyle.overlay}>
        <Animate type={AnimationTypes.slideUpIn} duration="0.3s">
          <div className={defaultStyle.alert2}>
            <TitlePageResponsiveIconSmall
              iconColor={THEME.colors.iconBGColor}
              title={t("label_ai_integration_settings_title")}
              icon={faBrain}
            >
              <div className="flex items-center gap-2">
                <PopupHelpButton
                  variant="global"
                  title={t("helpPopup:global.aiIntegration.title")}
                  description={t("helpPopup:global.aiIntegration.description")}
                  steps={t("helpPopup:global.aiIntegration.steps", { returnObjects: true }) as string[]}
                  bullets={t("helpPopup:global.aiIntegration.bullets", { returnObjects: true }) as string[]}
                />

                <ButtonIcon
                  sizeNumber={SIZE.buttonCloseIconSize}
                  bgColor={THEME.colors.buttonBGCloseColor}
                  iconColor={THEME.colors.buttonFGCloseColor}
                  icon={faClose}
                  onClick={onClose}
                  size="small"
                  descriptionKey="interactionDescriptions:pages.myRestaurant.aiIntegration.components.aiIntegrationSettingsPopup.button.onClose"
                />
              </div>
            </TitlePageResponsiveIconSmall>

            <div className="max-h-[72vh] overflow-x-hidden overflow-y-auto p-2">
              <div className="grid min-w-0 gap-3">
                <Animate type={AnimationTypes.fadeInTraslate} duration="1.2s">
                  <section className="rounded-lg border-2 border-sky-100 bg-sky-50 p-3">
                    <div className="flex flex-col items-center gap-3 text-justify sm:flex-row sm:items-start sm:text-left">
                      <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-white text-sky-700 sm:mt-0.5">
                        <FontAwesomeIcon icon={faBrain} />
                      </span>

                      <div className="min-w-0">
                        <strong className="block text-sm text-sky-900">
                          {t("label_ai_integration_popup_note_title")}
                        </strong>

                        <p className="mt-1 text-xs font-medium leading-relaxed text-sky-800">
                          {t("label_ai_integration_popup_note_description")}
                        </p>
                      </div>
                    </div>
                  </section>
                </Animate>

                <Animate type={AnimationTypes.fadeInTraslate} duration="1.3s">
                  <section className="rounded-lg border-2 border-slate-200 bg-white p-3">
                    <strong className="block text-sm text-slate-900">{t("label_ai_integration_provider")}</strong>

                    <div className="mt-3 grid gap-2 sm:grid-cols-2">
                      <Animate type={AnimationTypes.fadeInTraslate} duration="1.2s">
                        <button
                          type="button"
                          onClick={applyGptPreset}
                          className={`inline-flex items-center justify-center gap-2 rounded-lg border-2 px-3 py-2 text-xs font-bold transition ${
                            settings.provider === EAiIntegrationProvider.Gpt
                              ? "border-emerald-400 bg-emerald-50 text-emerald-700"
                              : "border-slate-200 bg-white text-slate-600 hover:bg-slate-50"
                          }`}
                          data-interaction-description-key="interactionDescriptions:pages.myRestaurant.aiIntegration.components.aiIntegrationSettingsPopup.button.applyGptPreset"
                        >
                          <FontAwesomeIcon icon={faRobot} />
                          {t("label_ai_integration_provider_gpt")}
                        </button>
                      </Animate>

                      <Animate type={AnimationTypes.fadeInTraslate} duration="1.2s" delay="0.06s">
                        <button
                          type="button"
                          onClick={applyCloudPreset}
                          className={`inline-flex items-center justify-center gap-2 rounded-lg border-2 px-3 py-2 text-xs font-bold transition ${
                            settings.provider === EAiIntegrationProvider.Cloud
                              ? "border-indigo-400 bg-indigo-50 text-indigo-700"
                              : "border-slate-200 bg-white text-slate-600 hover:bg-slate-50"
                          }`}
                          data-interaction-description-key="interactionDescriptions:pages.myRestaurant.aiIntegration.components.aiIntegrationSettingsPopup.button.applyCloudPreset"
                        >
                          <FontAwesomeIcon icon={faCloud} />
                          {t("label_ai_integration_provider_cloud")}
                        </button>
                      </Animate>
                    </div>
                  </section>
                </Animate>

                <Animate type={AnimationTypes.fadeInTraslate} duration="1.4s">
                  <section className="grid min-w-0 md:grid-cols-2">
                    <div className="min-w-0 p-3">
                      <SimpleToggle
                        title={t("label_ai_integration_enabled")}
                        value={settings.isEnabled}
                        onChange={(value) => patch({ isEnabled: value })}
                        descriptionKey="interactionDescriptions:pages.myRestaurant.aiIntegration.components.aiIntegrationSettingsPopup.toggle.aiIntegrationEnabled"
                      />
                    </div>

                    <div className="min-w-0 p-3">
                      <InputField
                        type="number"
                        icon={faBrain}
                        placeholder={t("label_ai_integration_months")}
                        value={String(settings.monthsToAnalyze)}
                        onChange={(event) => patchMonths(event.target.value)}
                        descriptionKey="interactionDescriptions:pages.myRestaurant.aiIntegration.components.aiIntegrationSettingsPopup.input.aiIntegrationMonths"
                      />
                    </div>

                    <div className="min-w-0 p-3">
                      <InputField
                        type="link"
                        icon={faLink}
                        placeholder={t("label_ai_integration_endpoint")}
                        value={settings.endpointUrl}
                        onChange={(event) => patch({ endpointUrl: event.target.value })}
                        descriptionKey="interactionDescriptions:pages.myRestaurant.aiIntegration.components.aiIntegrationSettingsPopup.input.aiIntegrationEndpoint"
                      />
                    </div>

                    <div className="min-w-0 p-3">
                      <InputField
                        type="password"
                        icon={faKey}
                        placeholder={t("label_ai_integration_api_key")}
                        value={settings.apiKey}
                        onChange={(event) => patch({ apiKey: event.target.value })}
                        descriptionKey="interactionDescriptions:pages.myRestaurant.aiIntegration.components.aiIntegrationSettingsPopup.input.aiIntegrationApiKey"
                      />
                    </div>

                    <div className="min-w-0 p-3 md:col-span-2">
                      <InputField
                        type="text"
                        icon={faRobot}
                        placeholder={t("label_ai_integration_model")}
                        value={settings.model}
                        onChange={(event) => patch({ model: event.target.value })}
                        descriptionKey="interactionDescriptions:pages.myRestaurant.aiIntegration.components.aiIntegrationSettingsPopup.input.aiIntegrationModel"
                      />
                    </div>
                  </section>
                </Animate>

                <Animate type={AnimationTypes.fadeInTraslate} duration="1.5s">
                  <div className="min-w-0 p-3">
                    <label className="mb-2 block text-sm font-medium text-gray-600">
                      {t("label_ai_integration_system_prompt")}
                    </label>

                    <textarea
                      className="w-full max-w-full rounded-lg border-2 border-slate-200 bg-white p-2 text-xs text-slate-700 outline-none transition-all duration-300 focus:border-sky-400"
                      rows={5}
                      maxLength={2000}
                      value={settings.systemPrompt}
                      onChange={(event) => patch({ systemPrompt: event.target.value })}
                      placeholder={t("label_ai_integration_system_prompt")}
                      data-interaction-description-key="interactionDescriptions:pages.myRestaurant.aiIntegration.components.aiIntegrationSettingsPopup.input.aiIntegrationSystemPrompt"
                    />

                    <p className="mt-1 text-right text-[11px] text-slate-400">{settings.systemPrompt.length}/2000</p>
                  </div>
                </Animate>

                <Animate type={AnimationTypes.fadeInTraslate} duration="1.6s">
                  <div className="flex justify-end">
                    <button
                      type="button"
                      onClick={onSave}
                      className="rounded-lg bg-emerald-600 px-4 py-2 text-sm font-bold text-white shadow-sm transition hover:bg-emerald-700"
                      data-interaction-description-key="interactionDescriptions:pages.myRestaurant.aiIntegration.components.aiIntegrationSettingsPopup.button.onSave"
                    >
                      {t("buttons:button_save")}
                    </button>
                  </div>
                </Animate>
              </div>
            </div>
          </div>
        </Animate>
      </div>
    </ModalPortal>
  )
}
